<template>
  <div v-show="hasMonths" class="calendar">
    <pitchtype-mini-availability-calendar-month
      v-for="([month, days], index) of sortedByMonth"
      :key="index"
      :month="month"
      :days="days"
      :month-format="monthFormat"
    />
  </div>
</template>

<script setup lang="ts">
import {
  addMonths,
  checkDatesAreTheSame,
  daysBetween,
  getMonthDifferenceBetweenTwoDates,
  isDayPast,
} from '~/utility/date/relative'
import {
  PitchtypeMiniAvailabilityCalendarDayInfo,
  PitchtypeMiniAvailabilityCalendarMonthsData,
  PitchtypeMiniAvailabilityCalendarStayDates,
} from './schemas'

import { computed, provide } from 'vue'
import { DatepickerAvailabilityResultsDay } from '../../../../datepicker-availability/types'

const props = withDefaults(
  defineProps<{
    datepickerAvailabilityResults?: DatepickerAvailabilityResultsDay[]
    stubData?: { month: number; year: number }
    stayDates?: PitchtypeMiniAvailabilityCalendarStayDates
    monthsToShow?: number
    monthFormat?: string
    calendarStartDate?: string
    showAsterix?: boolean
  }>(),
  {
    datepickerAvailabilityResults: () => [],
    monthsToShow: 3,
    monthFormat: 'long',
    showAsterix: false,
  },
)

provide(
  'stayDates',
  computed(() => props.stayDates),
)

const dateToStart = computed(() => {
  let dateToStart: Date | undefined

  if (!props.stubData && props.stayDates && props.stayDates.start) {
    dateToStart = new Date(props.stayDates.start)
    // When date is last day of month in month with 31 days
    // changing the month can have unexpected results. Adding will
    // roll forward to first of +2 months. Set date to mid month to
    // avoid issue.
    dateToStart.setDate(10)

    const today = new Date()
    if (
      dateToStart.getUTCFullYear() > today.getUTCFullYear() ||
      dateToStart.getUTCMonth() > today.getUTCMonth()
    ) {
      dateToStart.setUTCMonth(dateToStart.getUTCMonth() - 1)
    }
  } else if (props.calendarStartDate) {
    dateToStart = new Date(props.calendarStartDate)
    // See above
    dateToStart.setDate(10)
  }

  return dateToStart
})

const byMonth = computed(() => {
  return bringNumberOfMonthsToNormal(formattedMonthsList.value)
})

const sortedByMonth = computed(() => {
  return Object.entries(byMonth.value).sort(([firstMonth], [secondMonth]) =>
    Number(firstMonth) - Number(secondMonth) > 0 ? 1 : -1,
  )
})

const hasMonths = computed(() => Object.keys(byMonth.value).length > 0)

const listOfRawMonths = computed(() => {
  // TODO: Add proper type for reduce accumulator instead of any
  /* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return -- Legacy reduce with untyped accumulator */
  const defaultReduceValue: any = {
    months: [],
    activeIndex: 0,
    activeMonth: null,
  }
  return [...calendarData.value].reduce((res, dayAvailabilityData) => {
    const date = new Date(dayAvailabilityData.date)
    if (res.activeMonth === null) {
      res.activeMonth = date.getUTCMonth()
    }
    if (!res.months[res.activeIndex]) {
      res.months.push([])
    }
    if (res.activeMonth !== date.getUTCMonth()) {
      res.activeMonth = date.getUTCMonth()
      res.activeIndex += 1
      res.months.push([])
    }

    res.months[res.activeIndex].push(dayAvailabilityData)

    return res
  }, defaultReduceValue).months
  /* eslint-enable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return */
})

const filledListOfMonths = computed(() => {
  /* eslint-disable @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call -- Legacy computed with untyped month data */
  return listOfRawMonths.value
    .filter((month) => {
      return month.length > 0
    })
    .map((month) => {
      return generateMonthStubByDate(new Date(month[0].date)).map(({ date }) =>
        getFilledDayByDate(date, month),
      )
    })
  /* eslint-enable @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
})

const formattedMonthsList = computed(
  (): PitchtypeMiniAvailabilityCalendarMonthsData => {
    /* eslint-disable @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- Legacy computed with untyped month data */
    return filledListOfMonths.value
      .filter((month) => {
        return (
          dateToStart.value === undefined ||
          getTimestampOfDate(month[0].date) >=
            getTimestampOfDate(dateToStart.value)
        )
      })
      .slice(0, props.monthsToShow)
      .reduce((res, month) => {
        const key = getTimestampOfDate(month[0].date)
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Legacy reduce with untyped accumulator
        return { ...res, [key]: month }
      }, {})
    /* eslint-enable @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access */
  },
)

const calendarData = computed(() => props.datepickerAvailabilityResults || [])

const defaultDayData = computed(
  (): PitchtypeMiniAvailabilityCalendarDayInfo => {
    return {
      availAfterMinStay: false,
      availableForSearchTerms: false,
      date: '',
      hasAvailability: false,
      isSpecialOffer: false,
      minDays: null,
      selected: false,
    }
  },
)

function getTimestampOfDate(date: string | Date) {
  const dateObj = new Date(date)
  return Date.UTC(dateObj.getUTCFullYear(), dateObj.getUTCMonth(), 1)
}

function getFilledDayByDate(
  date: string,
  month: PitchtypeMiniAvailabilityCalendarDayInfo[],
) {
  const stubDate = new Date(date)
  const neededDate = month.find((day) =>
    checkDatesAreTheSame(stubDate, new Date(day.date)),
  )?.date
  const dayDate = neededDate || date

  return {
    ...defaultDayData.value,
    ...getAvailableDayData(neededDate),
    isWeekend: isWeekend(dayDate),
    isStartOfMonth: isStartOfMonth(dayDate),
    isSunday: isSunday(dayDate),
    isPast: isPast(dayDate),
    isMinDay: false,
    noArrivals: false,
    isAvailable: !!neededDate,
    date: dayDate,
    selected: isSelected(new Date(dayDate)),
    showAsterix: props.showAsterix,
  }
}

function getAvailableDayData(date?: string) {
  return date
    ? {
        availAfterMinStay: true,
        availableForSearchTerms: true,
        hasAvailability: true,
        isSpecialOffer: false,
        minDays: 0,
      }
    : {}
}

function getStubDate(): Date {
  return props.stubData
    ? new Date(Date.UTC(props.stubData.year, props.stubData.month, 1))
    : getNoStubDataStartDate()
}

function getNoStubDataStartDate() {
  return props.calendarStartDate && dateToStart.value
    ? new Date(dateToStart.value)
    : new Date()
}

function sortMonthsByTimeStamps(months: string[] | number[]): number[] {
  return months.map(Number).sort((a, b) => (a - b > 0 ? 1 : -1))
}

function bringNumberOfMonthsToNormal(
  data: PitchtypeMiniAvailabilityCalendarMonthsData,
) {
  const clonedData = { ...data }
  addStubsBeforeMonths(clonedData)
  addStubsBetweenMonths(clonedData)
  removeExtraMonths(clonedData)
  addStubsAfterMonths(clonedData)
  selectCorrectDates(clonedData)
  return clonedData
}

function addStubsBeforeMonths(
  data: PitchtypeMiniAvailabilityCalendarMonthsData,
) {
  const months = Object.keys(data)
  const sortedMonthsTimeStamps = sortMonthsByTimeStamps(months)

  if (months.length && dateToStart.value) {
    const firstAvailableMonthDate = new Date(sortedMonthsTimeStamps[0])
    const firstAvailableMonth = firstAvailableMonthDate.getUTCMonth()
    const firstAvailableYear = firstAvailableMonthDate.getUTCFullYear()

    const monthBeingAdded = new Date(dateToStart.value)

    while (
      monthBeingAdded.getUTCMonth() < firstAvailableMonth ||
      monthBeingAdded.getUTCFullYear() < firstAvailableYear
    ) {
      data[monthBeingAdded.getTime()] = generateMonthStubByDate(monthBeingAdded)
      monthBeingAdded.setUTCMonth(monthBeingAdded.getUTCMonth() + 1)
    }
  }
}

function addStubsBetweenMonths(
  data: PitchtypeMiniAvailabilityCalendarMonthsData,
) {
  const months = Object.keys(data)
  const sortedMonthsTimeStamps = sortMonthsByTimeStamps(months)

  if (months.length) {
    sortedMonthsTimeStamps.forEach((key, index) => {
      const nextMonthTimeStamp = sortedMonthsTimeStamps[index + 1]
      if (!nextMonthTimeStamp) {
        return
      }
      const date = new Date(key)
      const nextMonthDate = new Date(nextMonthTimeStamp)
      const monthDifference = getMonthDifferenceBetweenTwoDates(
        date,
        nextMonthDate,
      )

      for (let i = 1; i < monthDifference; i++) {
        const tempDate = new Date(date)
        tempDate.setUTCMonth(tempDate.getUTCMonth() + i)
        data[tempDate.getTime()] = generateMonthStubByDate(tempDate)
      }
    })
  }
}

function addStubsAfterMonths(
  calendarMonths: PitchtypeMiniAvailabilityCalendarMonthsData,
) {
  const months = Object.keys(calendarMonths)
  const hasMonths = months.length > 0
  const amountOfStubs = hasMonths
    ? Math.abs(months.length - props.monthsToShow) + 1
    : props.monthsToShow
  const stubDate = hasMonths
    ? new Date(sortMonthsByTimeStamps(months).pop() as number)
    : getStubDate()

  if (amountOfStubs > 0) {
    for (let index = hasMonths ? 1 : 0; index < amountOfStubs; index++) {
      const monthDate = addMonths(index, new Date(stubDate))
      calendarMonths[Number(monthDate)] = generateMonthStubByDate(monthDate)
    }
  }
}

function selectCorrectDates(data: PitchtypeMiniAvailabilityCalendarMonthsData) {
  for (const month in data) {
    for (const day in data[month]) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Legacy iteration over untyped calendar data
      const isDaySelected = isSelected(new Date(data[month][day].date))
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Legacy iteration over untyped calendar data
      data[month][day].selected = isDaySelected
    }
  }
}

function removeExtraMonths(data: PitchtypeMiniAvailabilityCalendarMonthsData) {
  const months = Object.keys(data)

  if (months.length > props.monthsToShow) {
    sortMonthsByTimeStamps(months)
      .splice(props.monthsToShow)
      .forEach((key) => delete data[key])
  }
}

function generateMonthStubByDate(date: Date) {
  const firstDate = getFirstDateOfMonth(date)
  const daysAmount = daysBetween(firstDate, getLastDateOfMonth(date))

  return new Array(daysAmount)
    .fill(null)
    .map((_, index) => generateDayDataWithIsoDate(firstDate, index))
}

function generateDayDataWithIsoDate(date: Date, index: number) {
  const tempDate = new Date(date)
  tempDate.setUTCDate(date.getUTCDate() + index)
  return {
    ...defaultDayData.value,
    date: tempDate.toISOString().split('T')[0],
  }
}

function getFirstDateOfMonth(date: Date) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1))
}

function getLastDateOfMonth(date: Date) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1))
}

function isSelected(date: Date) {
  let selected = false
  if (props.stayDates && props.stayDates.start && props.stayDates.end) {
    selected =
      new Date(props.stayDates.start).getTime() <= date.getTime() &&
      new Date(props.stayDates.end).getTime() >= date.getTime()
  }
  return selected
}

function isStartOfMonth(date: string) {
  return new Date(date).getDate() === 1
}

function isWeekend(date: string) {
  return new Date(date).getDay() % 6 === 0
}

function isSunday(date: string) {
  return new Date(date).getDay() === 0
}

function isPast(date: string) {
  return isDayPast(date)
}
</script>

<style scoped>
.calendar {
  display: flex;
  flex-wrap: nowrap;
  gap: 0.5em;
}
</style>
